import React from 'react';
import styled from 'styled-components';
import { DropMenu, type DropMenuItem, GetColor, Icon, Label, Radio, Tooltip } from 'venn-ui-kit';
import {
  assertExhaustive,
  FIGURE_ICONS,
  FIGURE_TEXT,
  getMaxSubjects,
  getSelectableMetricKeys,
  isHoldingsBlock,
  isPortfolioBlock,
  isTextOrFormatBlock,
  RowDisplayLimitType,
  supportsCompactMode,
  useHasFF,
} from 'venn-utils';
import {
  CorrelationLabelDisplayType,
  DataGridSizeType,
  GeoLevelFilterType,
  LegendPosition,
  PortfolioStrategyFilterType,
  SectorLevelFilterType,
  StudioTile,
  usePortfolioMultiFiltering,
} from 'venn-components';

import {
  type BlockId,
  blockInfoGraphicType,
  blockLogarithmicScale,
  blockMetrics,
  blockRowDisplayLimit,
  blockSettings,
  blockYAxisUnitFormat,
  correlationLabelDisplayType,
  geoLevelFilterType,
  isReportState,
  pieChartLegendPosition,
  portfolioStrategyFilterType,
  sectorLevelFilterType,
  UnitFormat,
  viewDataGridSizeType,
} from 'venn-state';
import { useRecoilState, useRecoilValue } from 'recoil';
import type { InfoGraphicTypeEnum } from 'venn-api';
import { PanelSection } from '../../../PanelSection';
import { StyledLabel } from '../../../shared';
import { DEFAULT_SCALE } from '../../../../logic/studioUtils';
import { ShowColumnChangesToggle } from './ShowColumnChangesToggle';
import { ScatterAxisSection } from './ScatterAxisSection';

interface FormatSectionProps {
  selected: BlockId;
  isReadOnly?: boolean;
}

const gridSizeOptions: DropMenuItem<DataGridSizeType>[] = [
  { label: 'Default', value: DataGridSizeType.DEFAULT },
  { label: 'Compact', value: DataGridSizeType.COMPACT },
];

const portfolioFilterOptions: DropMenuItem<PortfolioStrategyFilterType>[] = [
  { label: 'All Portfolio Constituents', value: PortfolioStrategyFilterType.DEFAULT },
  { label: 'Strategies Only', value: PortfolioStrategyFilterType.ALL_STRATEGIES },
  { label: 'Top Level Strategies', value: PortfolioStrategyFilterType.TOP_LEVEL_STRATEGIES },
];

const geoLevelFilterOptions: DropMenuItem<GeoLevelFilterType>[] = [
  { label: 'Region Level', value: GeoLevelFilterType.REGION },
  { label: 'Sub-Region Level', value: GeoLevelFilterType.SUB_REGION },
  { label: 'Country Level', value: GeoLevelFilterType.DEFAULT },
];

const sectorLevelFilterOptions: DropMenuItem<SectorLevelFilterType>[] = [
  { label: 'Top-Level Sectors', value: SectorLevelFilterType.TOP_LEVEL },
  { label: 'Sub-Sectors', value: SectorLevelFilterType.SUB_SECTOR },
  { label: 'Individual Sectors', value: SectorLevelFilterType.DEFAULT },
];

const legendPositionOptions: DropMenuItem<LegendPosition>[] = [
  { label: 'Left', value: LegendPosition.LEFT },
  { label: 'Right', value: LegendPosition.RIGHT },
  { label: 'Below', value: LegendPosition.BOTTOM },
];

const correlationLabelOptions: DropMenuItem<CorrelationLabelDisplayType>[] = [
  { label: 'Full', value: CorrelationLabelDisplayType.FULL },
  { label: 'Truncated', value: CorrelationLabelDisplayType.TRUNCATED },
];

const rowDisplayLimitOptions: DropMenuItem<RowDisplayLimitType>[] = [
  { label: 'All', value: RowDisplayLimitType.ALL },
  { label: 'Top 5', value: RowDisplayLimitType.TOP_5 },
  { label: 'Top 10', value: RowDisplayLimitType.TOP_10 },
];

const getTooltipStr = (
  infoGraphicType: InfoGraphicTypeEnum,
  selectedType: InfoGraphicTypeEnum | undefined,
  selectedMetricsCount: number,
  allowedMetrics: string[] | undefined,
): string | undefined => {
  if (infoGraphicType === selectedType) {
    return undefined;
  }

  switch (infoGraphicType) {
    case 'SCATTER':
      if (selectedMetricsCount > 2) {
        return 'Only two metrics will be displayed at a time';
      }
      return undefined;
    case 'LINE':
    case 'BAR':
    case 'DIVERGENT_BAR':
    case 'DISTRIBUTE_BAR':
    case 'FACTOR_TREND':
    case 'DRAWDOWN':
    case 'CORRELATION':
    case 'GROWTH_SIMULATION':
    case 'HEATMAP':
    case 'BOX':
    case 'PIE':
    case 'MAP':
    case 'AREA':
      if (allowedMetrics?.length !== 0 && selectedMetricsCount > 1) {
        return 'Charting is only available with 1 metric selected';
      }
      return undefined;
    case 'PIVOTED_GRID':
    case 'CALENDAR_GRID':
    case 'GRID':
    case 'GRID_TREE':
    case 'DEFAULT':
    case 'IMAGE':
    case 'PRIVATE_CASH_FLOW':
    case 'PEER_GROUP':
    case 'BOX_CHART':
      return undefined;
    default:
      throw assertExhaustive(infoGraphicType);
  }
};

const scaleOptions: DropMenuItem<boolean>[] = [
  { label: 'Logarithmic', value: true },
  { label: 'Linear', value: false },
];

const FormatSection = ({ selected, isReadOnly }: FormatSectionProps) => {
  const hasHoldingsMetadata = useHasFF('holdings_metadata');
  const { hasMultiLevelPortfolioFiltering } = usePortfolioMultiFiltering(selected);
  const settings = useRecoilValue(blockSettings(selected));
  const [infoGraphicType, setInfoGraphicType] = useRecoilState(blockInfoGraphicType(selected));
  const selectedMetrics = useRecoilValue(blockMetrics(selected));
  const [dataGridSize, setDataGridSize] = useRecoilState(viewDataGridSizeType(selected));
  const [portfolioStrategyFilter, setPortfolioStrategyFilter] = useRecoilState(portfolioStrategyFilterType(selected));
  const [geoLevelFilter, setGeoLevelFilter] = useRecoilState(geoLevelFilterType(selected));
  const [sectorLevelFilter, setSectorLevelFilter] = useRecoilState(sectorLevelFilterType(selected));
  const [legendPosition, setLegendPosition] = useRecoilState(pieChartLegendPosition(selected));
  const [rowDisplayLimit, setRowDisplayLimit] = useRecoilState(blockRowDisplayLimit(selected));
  const [correlationLabelDisplay, setCorrelationLabelDisplay] = useRecoilState(correlationLabelDisplayType(selected));
  const [yAxisFormat, setYAxisFormat] = useRecoilState(blockYAxisUnitFormat(selected));
  const [logarithmicScale, setLogarithmicScale] = useRecoilState(blockLogarithmicScale(selected));
  const availableTypes = settings.supportedGraphicTypes;
  const blockType = settings.customBlockType;
  const maxSubjects = getMaxSubjects(blockType, infoGraphicType, selectedMetrics);

  // Only show the selected graphic if it's in read only
  const graphicTypesToDisplay = availableTypes.filter(
    (type) => !!FIGURE_TEXT[type] && (!isReadOnly || infoGraphicType === type),
  );

  const onChangeDataGridSizeType = (option: DropMenuItem<DataGridSizeType>) => setDataGridSize(option.value);

  const onChangePortfolioStrategyFilterType = (option: DropMenuItem<PortfolioStrategyFilterType>) =>
    setPortfolioStrategyFilter(option.value);

  const onChangeGeoLevelFilterType = (option: DropMenuItem<GeoLevelFilterType>) => setGeoLevelFilter(option.value);
  const onChangeSectorLevelFilterType = (option: DropMenuItem<SectorLevelFilterType>) =>
    setSectorLevelFilter(option.value);
  const onChangeLegendPosition = (option: DropMenuItem<LegendPosition>) => setLegendPosition(option.value);
  const onChangeCorrelationLabelDisplayType = (option: DropMenuItem<CorrelationLabelDisplayType>) =>
    setCorrelationLabelDisplay(option.value);
  const onChangeRowDisplayLimit = (option: DropMenuItem<RowDisplayLimitType>) => setRowDisplayLimit(option.value);
  const isReport = useRecoilValue(isReportState);

  const blockAllowsGridStyleCustomization = supportsCompactMode(infoGraphicType, blockType);

  const allowsLogarithmicScale =
    ((blockType === 'PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN' && yAxisFormat === UnitFormat.ALLOCATION) ||
      blockType === 'PUBLIC_PRIVATE_ASSET_GROWTH_PERCENTILES' ||
      blockType === 'GROWTH_SIMULATION') &&
    !infoGraphicType?.includes('GRID');

  if (isTextOrFormatBlock(blockType)) {
    return null;
  }

  return (
    <PanelSection header="Format">
      <ButtonWrapper>
        {graphicTypesToDisplay.map((type) => {
          const hasMetricRestrictions = getSelectableMetricKeys(blockType, type);
          const tooltipStr = getTooltipStr(type, infoGraphicType, selectedMetrics.length, hasMetricRestrictions);

          return (
            <FormatWarningIconRoot key={type}>
              <StudioTile
                selected={infoGraphicType === type}
                onClick={isReadOnly ? undefined : () => setInfoGraphicType(type)}
                type={FIGURE_ICONS[type]}
                tooltip={FIGURE_TEXT[type]}
              />
              {tooltipStr && (
                <FormatWarningIcon>
                  <Tooltip content={tooltipStr} key={type} usePortal>
                    <Icon type="circle-exclamation" prefix="far" />
                  </Tooltip>
                </FormatWarningIcon>
              )}
            </FormatWarningIconRoot>
          );
        })}
      </ButtonWrapper>
      {!isReport && blockAllowsGridStyleCustomization && (
        <SectionPadding>
          <Label>Grid Size:</Label>
          <DropMenu<DataGridSizeType>
            items={gridSizeOptions}
            selected={dataGridSize ?? DataGridSizeType.DEFAULT}
            onChange={onChangeDataGridSizeType}
            disabled={isReadOnly}
          />
        </SectionPadding>
      )}

      {blockType === 'PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN' && !infoGraphicType?.includes('GRID') && (
        <YAxisFormatContainer>
          <Label>Y-axis:</Label>
          <Radio
            inputId="yAxisPercent"
            checked={yAxisFormat === UnitFormat.PERCENT}
            onChange={() => setYAxisFormat(UnitFormat.PERCENT)}
            value="yAxisPercent"
          >
            Percentage (%)
          </Radio>
          <Radio
            inputId="yAxisAllocation"
            checked={yAxisFormat === UnitFormat.ALLOCATION}
            onChange={() => setYAxisFormat(UnitFormat.ALLOCATION)}
            value="yAxisAllocation"
          >
            Value ($)
          </Radio>
        </YAxisFormatContainer>
      )}
      {allowsLogarithmicScale && (
        <SectionPadding>
          <StyledLabel>Scale:</StyledLabel>
          <DropMenu
            items={scaleOptions}
            selected={logarithmicScale ?? DEFAULT_SCALE}
            className="qa-logarithmic-scale"
            width={110}
            onChange={(item) => setLogarithmicScale(item.value)}
          />
        </SectionPadding>
      )}
      {infoGraphicType?.includes('GRID') &&
        !hasMultiLevelPortfolioFiltering &&
        (blockType === 'PORTFOLIO_CONTRIBUTION' || isPortfolioBlock(blockType)) && (
          <SectionPadding>
            {hasMultiLevelPortfolioFiltering ? null : (
              <>
                <Label>Portfolio Display Settings:</Label>
                <DropMenu<PortfolioStrategyFilterType>
                  items={portfolioFilterOptions}
                  selected={portfolioStrategyFilter ?? PortfolioStrategyFilterType.DEFAULT}
                  onChange={onChangePortfolioStrategyFilterType}
                  disabled={isReadOnly}
                />
              </>
            )}
          </SectionPadding>
        )}
      {infoGraphicType?.includes('GRID') && blockType === 'GEOGRAPHY_EXPOSURE' && (
        <SectionPadding>
          <Label>Display Settings:</Label>
          <DropMenu<GeoLevelFilterType>
            items={geoLevelFilterOptions}
            selected={geoLevelFilter ?? GeoLevelFilterType.DEFAULT}
            onChange={onChangeGeoLevelFilterType}
            disabled={isReadOnly}
          />
        </SectionPadding>
      )}
      {infoGraphicType?.includes('PIE') &&
        blockType === 'PORTFOLIO_BREAKDOWN' &&
        hasMultiLevelPortfolioFiltering &&
        isReport && (
          <SectionPadding>
            <Label>Legend Position</Label>
            <DropMenu<LegendPosition>
              items={legendPositionOptions}
              selected={legendPosition ?? LegendPosition.RIGHT}
              onChange={onChangeLegendPosition}
              disabled={isReadOnly}
            />
          </SectionPadding>
        )}
      {infoGraphicType?.includes('GRID') && blockType === 'SECTOR_EXPOSURE' && (
        <SectionPadding>
          <Label>Display Settings:</Label>
          <DropMenu<SectorLevelFilterType>
            items={sectorLevelFilterOptions}
            selected={sectorLevelFilter ?? SectorLevelFilterType.DEFAULT}
            onChange={onChangeSectorLevelFilterType}
            disabled={isReadOnly}
          />
        </SectionPadding>
      )}
      {blockType === 'MUNI_BREAKDOWN' && (
        <SectionPadding>
          <Label>Display Settings:</Label>
          <DropMenu<RowDisplayLimitType>
            items={rowDisplayLimitOptions}
            selected={rowDisplayLimit ?? RowDisplayLimitType.ALL}
            onChange={onChangeRowDisplayLimit}
            disabled={isReadOnly}
          />
        </SectionPadding>
      )}
      {blockType === 'CORRELATION' && (
        <SectionPadding>
          <Label>Label Display:</Label>
          <DropMenu<CorrelationLabelDisplayType>
            items={correlationLabelOptions}
            selected={correlationLabelDisplay ?? CorrelationLabelDisplayType.TRUNCATED}
            onChange={onChangeCorrelationLabelDisplayType}
            disabled={isReadOnly}
          />
        </SectionPadding>
      )}
      {hasHoldingsMetadata && isHoldingsBlock(blockType) && maxSubjects > 1 && (
        <div className="mt-[16px]">
          <ShowColumnChangesToggle selectedRefId={selected} />
        </div>
      )}
      {infoGraphicType === 'SCATTER' && (
        <ScatterAxisSection key={selected} selectedBlockId={selected} isReadOnly={isReadOnly} />
      )}
    </PanelSection>
  );
};

export default FormatSection;

const ButtonWrapper = styled.div`
  display: flex;
  column-gap: 10px;
  margin-top: 10px;
`;

const FormatWarningIconRoot = styled.div`
  position: relative;
`;

const FormatWarningIcon = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  height: 12px;
  border-radius: 50%;
  background-color: ${GetColor.White};
  color: ${GetColor.Primary.Dark};
`;

const SectionPadding = styled.div`
  padding-top: 10px;
`;

const YAxisFormatContainer = styled.div`
  padding-top: 10px;
  font-size: 14px;
  display: flex;
  font-weight: bold;
  flex-direction: column;
`;
